import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { LIVE_RESULTS_STATUS, LiveResultsRankingsFromApi } from '@root/src/types';
import api from '@root/src/services/api';
import useLazyLoading from '@root/src/hooks/useLazyLoading';
import { pushLiveResultsRankings, selectLiveResultsOptionFromTypeId, selectResultsListFilter } from '@features/results/slices/resultsSlice';
import { selectEventCategories, selectEventDetail } from '@features/eventDetail/slices/eventDetailServerSlice';
import { searchIsValid } from '@features/ui/components/SearchInput';
import { getSearchOptions } from '@features/results/components/EventLiveResultsList';

const useLiveResultsLazyLoading = ({
  eventId,
  queryTypeId,
}: {
  eventId: number,
  queryTypeId: number,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const event = useSelector(selectEventDetail(eventId));
  const categories = useSelector(selectEventCategories(eventId));

  const resultsFilter = useSelector(selectResultsListFilter);
  const searchOptions = getSearchOptions(t, [], []);
  const isLive = event.hasLiveResults === LIVE_RESULTS_STATUS.ONLINE;

  const resultsListSelectedOption = useSelector(
    selectLiveResultsOptionFromTypeId(queryTypeId)
  );
  const resultsListSelectedOptionRef = useRef(resultsListSelectedOption);

  useEffect(() => {
    resultsListSelectedOptionRef.current = resultsListSelectedOption;
  }, [resultsListSelectedOption]);

  const endpoint = api.results.live.events.rankings.list;
  const processData = (json: any, overrideCurrent?: boolean) => {
    dispatch(pushLiveResultsRankings({
      liveResultsFromApi: json,
      currentOption: resultsListSelectedOptionRef.current,
      overrideCurrent,
      categories,
    }));
  };
  const params = {
    eventId,
    rankingId: resultsListSelectedOption.wiclaxRankingId,
    filterBy: {
      ...(resultsListSelectedOption.categoryId ? { category: resultsListSelectedOption.categoryId } : {}),
      ...resultsFilter.query === '' ? {} : { [resultsFilter.filterBy]: resultsFilter.query },
    },
  };
  const fetchIsValid = searchIsValid(
    searchOptions.find(option => option.value === resultsFilter.filterBy)?.type,
    resultsFilter.query,
  );
  const { observerLoadMoreRef, isLoading, hasMore } = useLazyLoading({
    endpoint,
    params,
    processData,
    checkIfHasNoMoreData,
    resetListDependencies: useMemo(() => ([resultsFilter]), [resultsFilter]),
    fetchIsValid,
    autoRefresh: isLive,
  });

  return {
    observerLoadMoreRef,
    isLoading,
    hasMore,
  };
};

export default useLiveResultsLazyLoading;

const checkIfHasNoMoreData = (json: LiveResultsRankingsFromApi, pageSize: number) => {
  const numberOfResults = Object.values(json.results).flat().length;
  if (numberOfResults === 0 || numberOfResults < pageSize) {
    return true;
  }
  return false;
};
